import { Address } from "@unlockdfinance/verislabs-web3";
import useModalHelper from "components/modals/hooks/useModalHelper";
import { useCustomWallet } from "components/providers";
import CurrenciesModule from "logic/CurrenciesModule";
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

type IAccountContext = {
  isQrModalOpen: boolean;
  toggleModalQr: () => void;
  tokenBalances: Map<string, bigint>;
  updateBalances: () => void;
};

const TOKENS = CurrenciesModule.tokens;
const DEFAULT_TOKEN_BALANCES = new Map(
  TOKENS.map(({ name }) => [name, BigInt(0)])
);

const AccountContext = createContext<IAccountContext>({
  isQrModalOpen: false,
  toggleModalQr: () => {},
  tokenBalances: DEFAULT_TOKEN_BALANCES,
  updateBalances: () => {},
});

export const AccountProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isQrModalOpen, setQrModalOpen] = useModalHelper();
  const [tokenBalances, setTokenBalances] = useState(DEFAULT_TOKEN_BALANCES);
  const { smartAddress } = useCustomWallet();
  const [sensor, toggle] = useState<boolean>(false);

  useEffect(() => {
    if (smartAddress) {
      handleGetTokenBalances(smartAddress);

      return () => setTokenBalances(DEFAULT_TOKEN_BALANCES);
    }
  }, [smartAddress, sensor]);

  const handleGetTokenBalances = async (address: Address) => {
    const balances = await Promise.all(
      TOKENS.map((token) => token.getWalletBalance(address))
    );

    setTokenBalances(new Map(TOKENS.map(({ name }, i) => [name, balances[i]])));
  };

  const toggleModalQr = () => setQrModalOpen(!isQrModalOpen);

  const updateBalances = () => toggle(!sensor);

  return (
    <AccountContext.Provider
      value={{ isQrModalOpen, toggleModalQr, tokenBalances, updateBalances }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useModalQr = (): [boolean, () => void] => {
  const { isQrModalOpen, toggleModalQr } = useContext(AccountContext);

  return [isQrModalOpen, toggleModalQr];
};

export const useTokenBalances = () => {
  const { tokenBalances } = useContext(AccountContext);

  return tokenBalances;
};

export const useUpdateTokenBalances = () => {
  const { updateBalances } = useContext(AccountContext);

  return updateBalances;
};
